import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import './VoiceChatHistory.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

const VoiceChatHistory = ({ isOpen, onClose }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  const fetchSessions = useCallback(async (search = '') => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('limit', '50');

      const response = await fetch(
        `${BACKEND_URL}/api/voice-chat?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch sessions');
      }

      const data = await response.json();
      setSessions(data.sessions || []);
    } catch (err) {
      console.error('Error fetching voice chat sessions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSessionDetails = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${BACKEND_URL}/api/voice-chat/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch session details');
      }

      const data = await response.json();
      setSelectedSession(data.session);
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error fetching session details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSession = useCallback(async (sessionId) => {
    if (!window.confirm('Delete this voice chat? This cannot be undone.')) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${BACKEND_URL}/api/voice-chat/${sessionId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete session');
      }

      // Refresh sessions list
      fetchSessions(searchQuery);
      if (selectedSession?.id === sessionId) {
        setSelectedSession(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Error deleting session:', err);
      setError(err.message);
    }
  }, [fetchSessions, searchQuery, selectedSession]);

  useEffect(() => {
    if (isOpen) {
      fetchSessions();
    }
  }, [isOpen, fetchSessions]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (isOpen) {
        fetchSessions(searchQuery);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, isOpen, fetchSessions]);

  const handleBackToList = () => {
    setSelectedSession(null);
    setMessages([]);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could show a toast here
    });
  };

  const exportSession = () => {
    if (!selectedSession || !messages.length) return;

    const content = messages
      .map((msg) => `[${msg.role.toUpperCase()}] ${msg.content}`)
      .join('\n\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `voice-chat-${selectedSession.id.slice(0, 8)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="voice-history-overlay" onClick={onClose}>
      <div className="voice-history-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="voice-history-header">
          {selectedSession ? (
            <>
              <button className="voice-history-back-btn" onClick={handleBackToList}>
                <i className="bi bi-arrow-left"></i>
              </button>
              <h2>{selectedSession.title}</h2>
              <div className="voice-history-header-actions">
                <button
                  className="voice-history-action-btn"
                  onClick={exportSession}
                  title="Export as text"
                >
                  <i className="bi bi-download"></i>
                </button>
                <button
                  className="voice-history-action-btn danger"
                  onClick={() => deleteSession(selectedSession.id)}
                  title="Delete"
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </>
          ) : (
            <>
              <h2>Voice Chat History</h2>
              <button className="voice-history-close-btn" onClick={onClose}>
                <i className="bi bi-x-lg"></i>
              </button>
            </>
          )}
        </div>

        {/* Search - only in list view */}
        {!selectedSession && (
          <div className="voice-history-search">
            <i className="bi bi-search"></i>
            <input
              type="text"
              placeholder="Search voice chats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="voice-history-clear-btn"
                onClick={() => setSearchQuery('')}
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="voice-history-content">
          {loading && (
            <div className="voice-history-loading">
              <div className="spinner"></div>
              <span>Loading...</span>
            </div>
          )}

          {error && (
            <div className="voice-history-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && !selectedSession && sessions.length === 0 && (
            <div className="voice-history-empty">
              <i className="bi bi-mic"></i>
              <h3>No voice chats yet</h3>
              <p>Your voice chat transcripts will appear here</p>
            </div>
          )}

          {/* Sessions List */}
          {!selectedSession && sessions.length > 0 && (
            <div className="voice-history-list">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className="voice-history-item"
                  onClick={() => fetchSessionDetails(session.id)}
                >
                  <div className="voice-history-item-icon">
                    <i className="bi bi-mic-fill"></i>
                  </div>
                  <div className="voice-history-item-content">
                    <div className="voice-history-item-title">{session.title}</div>
                    <div className="voice-history-item-meta">
                      <span>{formatDate(session.started_at)}</span>
                      <span className="separator">•</span>
                      <span>{formatDuration(session.duration_seconds)}</span>
                      <span className="separator">•</span>
                      <span>{session.message_count} messages</span>
                    </div>
                  </div>
                  <button
                    className="voice-history-item-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSession(session.id);
                    }}
                    title="Delete"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Session Detail / Transcript */}
          {selectedSession && (
            <div className="voice-history-transcript">
              <div className="voice-history-session-info">
                <span>{formatDate(selectedSession.started_at)}</span>
                <span className="separator">•</span>
                <span>{formatDuration(selectedSession.duration_seconds)}</span>
                <span className="separator">•</span>
                <span>{selectedSession.model}</span>
              </div>
              
              {messages.length === 0 ? (
                <div className="voice-history-empty">
                  <p>No transcripts available for this session</p>
                </div>
              ) : (
                <div className="voice-history-messages">
                  {messages.map((message, index) => (
                    <div
                      key={message.id || index}
                      className={`voice-history-message ${message.role}`}
                    >
                      <div className="voice-history-message-header">
                        <span className="voice-history-message-role">
                          {message.role === 'user' ? 'You' : 'Zoomey'}
                        </span>
                        <button
                          className="voice-history-copy-btn"
                          onClick={() => copyToClipboard(message.content)}
                          title="Copy"
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                      <div className="voice-history-message-content">
                        {message.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceChatHistory;
